/**
 * Mutation Utilities
 *
 * Standardized mutation patterns for consistent data refresh behavior
 * across all services. Ensures immediate UI updates after mutations.
 */

import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
  UseMutationResult,
  QueryKey
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';

// Toast deduplication to prevent duplicates in StrictMode
const toastDedupeMap = new Map<string, number>();
const TOAST_DEDUPE_TIME = 500; // ms

function showToastWithDedupe(type: 'success' | 'error', message: string) {
  const key = `${type}:${message}`;
  const lastShown = toastDedupeMap.get(key);
  const now = Date.now();

  // Skip if same toast was shown within dedupe time
  if (lastShown && now - lastShown < TOAST_DEDUPE_TIME) {
    return;
  }

  toastDedupeMap.set(key, now);
  toast[type](message);

  // Clean up old entries periodically
  if (toastDedupeMap.size > 100) {
    const cutoff = now - TOAST_DEDUPE_TIME * 2;
    const entriesToDelete: string[] = [];
    toastDedupeMap.forEach((time, key) => {
      if (time < cutoff) {
        entriesToDelete.push(key);
      }
    });
    entriesToDelete.forEach((key) => toastDedupeMap.delete(key));
  }
}

/**
 * Configuration for creating standardized mutations
 */
export interface MutationConfig<TData, TVariables, TContext = unknown> {
  /** The async function that performs the mutation */
  mutationFn: (variables: TVariables) => Promise<TData>;

  /** Query keys to invalidate after successful mutation */
  invalidateKeys?: QueryKey[] | ((variables: TVariables) => QueryKey[]);

  /** Query keys to refetch after successful mutation */
  refetchKeys?: QueryKey[] | ((variables: TVariables) => QueryKey[]);

  /** Function to optimistically update the cache */
  optimisticUpdate?: (variables: TVariables) => TContext;

  /** Generate success message from response data, variables, and context */
  onSuccessMessage?: (
    data: TData,
    variables: TVariables,
    context: TContext | undefined
  ) => string;

  /** Error message to show on failure */
  onErrorMessage?: string;

  /** Whether to show toast notifications */
  showToast?: boolean;
}

/**
 * Creates a standardized mutation hook with automatic cache management
 *
 * @example
 * ```typescript
 * const useCreateItem = createMutationHook({
 *   mutationFn: (data) => api.createItem(data),
 *   refetchKeys: [['items', 'list']],
 *   onSuccessMessage: (item) => `Created ${item.name}`
 * });
 * ```
 */
export function createMutationHook<
  TData = unknown,
  TVariables = void,
  TContext = unknown
>(
  config: MutationConfig<TData, TVariables, TContext>
): (
  options?: Omit<
    UseMutationOptions<TData, Error, TVariables, TContext>,
    'mutationFn'
  >
) => UseMutationResult<TData, Error, TVariables, TContext> {
  return function useMutationHook(
    options?: Omit<
      UseMutationOptions<TData, Error, TVariables, TContext>,
      'mutationFn'
    >
  ) {
    const queryClient = useQueryClient();
    const showToast = config.showToast ?? true;

    return useMutation<TData, Error, TVariables, TContext>({
      mutationFn: config.mutationFn,

      onMutate: async (variables) => {
        // Resolve keys if they are functions
        const invalidateKeys =
          typeof config.invalidateKeys === 'function'
            ? config.invalidateKeys(variables)
            : config.invalidateKeys || [];
        const refetchKeys =
          typeof config.refetchKeys === 'function'
            ? config.refetchKeys(variables)
            : config.refetchKeys || [];

        // Cancel in-flight queries to prevent race conditions
        const cancelPromises = [
          ...invalidateKeys.map((key) =>
            queryClient.cancelQueries({ queryKey: key })
          ),
          ...refetchKeys.map((key) =>
            queryClient.cancelQueries({ queryKey: key })
          )
        ];

        await Promise.all(cancelPromises);

        // Perform optimistic update if provided
        if (config.optimisticUpdate) {
          return config.optimisticUpdate(variables);
        }

        return undefined as TContext;
      },

      onSuccess: async (data, variables, context) => {
        // Resolve keys if they are functions
        const invalidateKeys =
          typeof config.invalidateKeys === 'function'
            ? config.invalidateKeys(variables)
            : config.invalidateKeys || [];
        const refetchKeys =
          typeof config.refetchKeys === 'function'
            ? config.refetchKeys(variables)
            : config.refetchKeys || [];

        // Invalidate stale queries first
        if (invalidateKeys.length) {
          await Promise.all(
            invalidateKeys.map((key) =>
              queryClient.invalidateQueries({
                queryKey: key,
                exact: false
              })
            )
          );
        }

        // Then refetch for immediate updates
        if (refetchKeys.length) {
          await Promise.all(
            refetchKeys.map((key) =>
              queryClient.refetchQueries({
                queryKey: key,
                exact: false,
                type: 'active' // Only refetch visible queries
              })
            )
          );
        }

        // Show success notification with deduplication
        if (showToast && config.onSuccessMessage) {
          showToastWithDedupe(
            'success',
            config.onSuccessMessage(data, variables, context)
          );
        }

        // Call custom onSuccess if provided
        await options?.onSuccess?.(data, variables, context);
      },

      onError: async (error, variables, context) => {
        // Discard any optimistic write by invalidating the same keys
        // onSuccess would — forces a refetch of server truth instead of
        // leaving the UI showing the optimistic (wrong) result after a
        // failed mutation. A blanket context.snapshot restore is unsafe
        // here: some hooks fan an optimistic write across multiple cache
        // keys that a single snapshot doesn't cover.
        const invalidateKeys =
          typeof config.invalidateKeys === 'function'
            ? config.invalidateKeys(variables)
            : config.invalidateKeys || [];
        if (invalidateKeys.length) {
          await Promise.all(
            invalidateKeys.map((key) =>
              queryClient.invalidateQueries({
                queryKey: key,
                exact: false
              })
            )
          );
        }

        // Show error notification with deduplication
        // Prefer actual error message from backend over generic fallback
        if (showToast) {
          const message =
            error.message ||
            config.onErrorMessage ||
            'An error occurred. Please try again.';
          showToastWithDedupe('error', message);
        }

        // Call custom onError if provided
        await options?.onError?.(error, variables, context);
      },

      onSettled: async (data, error, variables, context) => {
        // Call custom onSettled if provided
        await options?.onSettled?.(data, error, variables, context);
      },

      // Spread non-lifecycle options only. Spreading lifecycle handlers
      // here would clobber the wrappers above — including the
      // `invalidateKeys` invalidation in onSuccess. The user's
      // onMutate/onSuccess/onError/onSettled are already invoked from
      // inside the wrappers; they don't need to be re-applied here.
      ...(() => {
        if (!options) return {};
        const {
          onMutate: _onMutate,
          onSuccess: _onSuccess,
          onError: _onError,
          onSettled: _onSettled,
          ...rest
        } = options;
        return rest;
      })()
    });
  };
}

/**
 * Helper to create optimistic update context with rollback support
 */
export function createOptimisticContext<T>(
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: QueryKey
): {
  snapshot: T | undefined;
  update: (updater: (old: T | undefined) => T | undefined) => void;
  rollback: () => void;
} {
  const snapshot = queryClient.getQueryData<T>(queryKey);

  return {
    snapshot,
    update: (updater) => {
      queryClient.setQueryData<T>(queryKey, updater);
    },
    rollback: () => {
      if (snapshot !== undefined) {
        queryClient.setQueryData(queryKey, snapshot);
      }
    }
  };
}

/**
 * Type-safe mutation result extractor
 */
export function extractMutationData<T>(result: {
  success: boolean;
  data?: T;
  error?: string;
}): T {
  if (!result.success || !result.data) {
    throw new Error(result.error || 'Operation failed');
  }
  return result.data;
}
